import { defaultTheme, RaThemeOptions } from 'react-admin';
import type { ChipProps, TextFieldProps, ThemeOptions } from '@mui/material';
import merge from 'lodash.merge';
import { alpha, createTheme, responsiveFontSizes } from '@mui/material/styles';
import { Theme } from '@mui/system';
import { PaymentStatus } from '@football-kids/shared/enums';
import { tint } from '@football-kids/client-lib/utils/colors';

declare module '@mui/system' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface Theme {
        sidebar: {
            width: number;
            closedWidth: number;
            mobileHeight: number; // as Mui spacing
        };
        // shadows: typeof theme["shadows"]
    }
}

export const primaryColor = '#487048';

const theme: ThemeOptions &
    RaThemeOptions & {
        sidebar: { mobileHeight: number | string };
        palette: {
            paymentStatus: Record<string, any>;
        };
    } = {
    palette: {
        primary: {
            main: primaryColor, // '#7DA031', // '#3b755f', //  "#53b1c8", //
            contrastText: '#fff',
        },
        secondary: {
            main: '#f2cb6c',
            contrastText: '#000000',
        },
        error: {
            main: '#DA2F43',
        },
        warning: {
            light: 'rgb(243,165,39)',
            main: '#ed6c02',
            dark: '#d46305',
        },
        success: {
            light: tint(primaryColor, 12), // '#c1e0a2',
            main: primaryColor, // '#b3d981',
            dark: primaryColor, // '#9fc073',
            contrastText: '#fff',
        },
        background: {
            paper: '#fff', // '#F7FAEF',
            default: '#f9faf7',
        },
        paymentStatus: {
            [PaymentStatus.NEW]: {
                main: 'rgba(0, 0, 0, 0.6)',
                contrastText: '#fff',
            },
            [PaymentStatus.SUCCESS]: {
                main: '#0C8346',
                contrastText: '#fff',
            },
            [PaymentStatus.PENDING]: {
                main: 'rgba(0, 0, 0, 0.6)', // '#CA7D2B',
                contrastText: '#fff',
            },
            [PaymentStatus.FAILURE]: {
                main: '#DA2F43',
                contrastText: '#fff',
            },
            [PaymentStatus.PARTLY]: {
                main: '#e0bf0d',
                contrastText: 'rgba(0, 0, 0, 0.9)',
            },
            [PaymentStatus.CANCELLED]: {
                main: '#B3569D',
                contrastText: '#fff',
            },
        },
    },
    typography: {
        h1: {
            fontSize: '2.25rem',
            fontWeight: 500,
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 500,
        },
        h3: {
            fontSize: '1.75rem',
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 500,
        },
        h5: {
            fontSize: '1.25rem',
        },
        h6: {
            fontSize: '1rem',
        },
        subtitle1: {
            fontWeight: 500,
            lineHeight: '1.3',
        },
    },
    sidebar: {
        width: 260,
        closedWidth: 48 + 2 * 8,
        mobileHeight: 8,
    },
    shadows: [
        'none',
        ``,
        `0 1px 2px 0 rgb(0 0 0 / 0.05)`,
        ``,
        `0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)`, // 4
        ``, //5
        ``,
        ``,
        `0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)`,
        ``,
        ``,
        ``,
        `0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)`,
        ``,
        ``,
        ``,
        `0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)`,
        ``,
        ``,
        ``,
        `0 25px 50px -12px rgb(0 0 0 / 0.25)`,
        ``,
        ``,
        ``,
        ``,
    ],
    components: {
        MuiAutocomplete: {
            defaultProps: {
                fullWidth: true,
                componentsProps: {
                    paper: {
                        elevation: 12,
                    },
                },
            },
            styleOverrides: {
                paper: ({ theme }) =>
                    theme.unstable_sx({
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderLeft: `2px solid`,
                        borderRight: `2px solid`,
                        borderImage: `linear-gradient(
    to bottom,
    ${theme.palette.primary.main},
    rgba(0, 0, 0, 0)
) 1 100%`,
                    }),
            },
        },
        MuiAvatar: {
            defaultProps: {
                variant: 'rounded',
            },
            styleOverrides: {
                rounded: ({ theme }) =>
                    theme.unstable_sx({
                        borderRadius: 2,
                    }),
            },
        },
        MuiAppBar: {
            defaultProps: {
                color: 'transparent',
                elevation: 0,
            },
            styleOverrides: {
                root: {},
            },
        },
        MuiLink: {
            defaultProps: {
                underline: 'hover',
            },
        },
        MuiButton: {
            styleOverrides: {
                root: ({ theme }) =>
                    theme.unstable_sx({
                        borderRadius: 6,
                    }),
                sizeMedium: ({ theme }) =>
                    theme.unstable_sx({
                        py: 1,
                    }),
                sizeLarge: {
                    padding: `12px 40px`,
                },
                outlinedPrimary: ({ theme }) =>
                    theme.unstable_sx({
                        borderColor: `1px solid #ccc`,
                    }),
                contained: {
                    //                     background: `linear-gradient(145deg, #72f0a6, #60ca8c)`,
                    // boxShadow:  `20px 20px 58px #5bbe84,
                    //     -20px -20px 58px #7bffb2`
                },

                // textSizeMedium: ({ theme }) =>
                //     theme.unstable_sx({
                //         px: 2,
                //     }),
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: ({ theme }) =>
                    theme.unstable_sx({
                        px: 1,
                        [theme.breakpoints.up('sm')]: {
                            px: 2,
                        },
                        [theme.breakpoints.up('xl')]: {
                            px: 4,
                        },
                    }),
            },
        },
        MuiCard: {
            defaultProps: {
                variant: 'outlined',
            },
            styleOverrides: {
                root: {},
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: ({ theme }) =>
                    theme.unstable_sx({
                        p: {
                            xs: 2,
                            // sm: 4,
                            xl: 4,
                        },
                        '&:last-child': {
                            // todo na od md 4 a wczesniej 2
                            pb: {
                                xs: 2,
                                // sm: 4,
                                xl: 4,
                            },
                        },
                    }),
            },
        },
        MuiChip: {
            defaultProps: {} as ChipProps,
            styleOverrides: {
                root: ({ theme }) =>
                    theme.unstable_sx({
                        borderRadius: 2,
                    }),
            },
        },
        MuiTableRow: {
            styleOverrides: {
                hover: ({ theme }) =>
                    theme.unstable_sx({
                        '&:hover': {
                            '& > td': {
                                overflow: 'hidden',
                                background: theme.palette.action.hover,
                            },
                        },
                    }),
            },
        },
        MuiDialogTitle: {
            defaultProps: {
                variant: 'h4',
            },
        },
        MuiTextField: {
            defaultProps: {
                fullWidth: true,
                variant: 'outlined' as const,
                InputLabelProps: {
                    shrink: true,
                },
            } as TextFieldProps,
            styleOverrides: {
                root: ({ theme }) =>
                    theme.unstable_sx({
                        mt: `${23 + 8}px`,

                        '.MuiInputLabel-root': {
                            transform: 'none',
                            transition: 'none',
                            bottom: `calc(100% + ${8}px)`,
                            top: 'unset',
                        },
                        '.MuiOutlinedInput-notchedOutline': {
                            top: -2.5,
                            bottom: -2.5,

                            legend: {
                                display: 'none',
                            },
                        },

                        '.MuiFormHelperText-root': {
                            mx: 0,
                        },
                    }),
            },
        },
        MuiCheckbox: {
            // todo obecnosci w grupie
            //styleOverrides: {
            //    root: {
            //        '& .MuiSvgIcon-root': { fontSize: 38 }
            //    }
            //}
        },
        MuiPaginationItem: {
            styleOverrides: {},
        },
        MuiPaper: {
            defaultProps: {},
            styleOverrides: {
                root: {},
                rounded: {
                    // InnerBordeR = OouterBR - BorderWidth
                    borderRadius: 8 * 6 - 8 * 3,
                },
                outlined: ({ theme }) =>
                    theme.unstable_sx({
                        borderColor: alpha(theme.palette.divider, 0.06),
                    }),
            },
        },
        RaToolbar: {
            defaultProps: {
                color: 'inherit',
            },
            styleOverrides: {
                // root: {
                //     RaToolbar-root.RaToolbar-mobileToolbar
                // },

                root: ({ theme }) =>
                    theme.unstable_sx({
                        bgcolor: 'rgba(0, 0, 0, 0.03)',

                        '&.RaToolbar-mobileToolbar': {
                            bottom: 8 * 8,
                            bgcolor: 'background.paper',
                            borderTop: `1px solid`,
                            borderColor: alpha(theme.palette.divider, 0.06),
                        },
                    }),
            },
        },
        RaTopToolbar: {
            styleOverrides: {
                root: {
                    minHeight: 52,
                    alignItems: 'center',
                },
            },
        }, //RaList-content css-13u09tt-MuiPaper-root-MuiCard-root"
        RaList: {
            styleOverrides: {
                root: {
                    width: '100%',
                },
            },
        },
        RaSimpleForm: {
            styleOverrides: {
                root: {
                    '& > .MuiStack-root': {
                        alignItems: 'stretch',
                    },
                },
            },
        },
        RaTabbedForm: {
            styleOverrides: {
                root: {
                    '& .MuiCardContent-root > .MuiStack-root': {
                        alignItems: 'stretch',
                    },
                },
            },
        },
        RaCreateButton: {
            styleOverrides: {
                root: ({ theme }) =>
                    theme.unstable_sx({
                        '&.RaCreateButton-floating': {
                            bottom: theme.spacing(
                                (
                                    theme as unknown as {
                                        sidebar: { mobileHeight: number };
                                    }
                                ).sidebar.mobileHeight + 2
                            ),
                            left: theme.spacing(2),
                        },
                    }),
            },
        },
        RaSaveButton: {
            styleOverrides: {
                root: {
                    minWidth: 180,
                },
            },
        },
    },
};

export const globalStyles = (theme: Theme) => ({
    fieldset: {
        border: 'none',
        padding: 0,
    },
    '.sentry-error-embed.sentry-error-embed': {
        '*': {
            fontFamily: 'Roboto',
            ...((theme.typography as any)?.body1 as any),
        },

        'h2': {
            ...((theme.typography as any)?.h4 as any),
        },

        'p': {
            color: theme.palette.text.secondary,
        },



        '.form-submit': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',

            '.btn': {
                ...((theme.typography as any)?.button as any),
                border: 'none',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',

                color: '#fff',
                backgroundColor: '#487048',

                '&:hover, &:focus, &:active': {
                    backgroundColor: '#375637',
                },

                borderRadius: '24px',
                padding: '8px 24px',
            },

            '.close': {
                ...((theme.typography as any)?.button as any),
                padding: '6px 16px',
                marginLeft: '8px',
            }
        },

        '.powered-by': {
            display: 'none',
        },
    },
    ...theme.unstable_sx({
        '#formbricks-modal-container': {
            '--fb-border-radius': '24px',
            '.fb-text-signature': {
                display: 'none',
            },
        },
        '#fbjs *, #fbjs ::before, #fbjs ::after': {
            fontFamily: 'Roboto',
            ...((theme.typography as any)?.body1 as any),

            '.fb-border-submit-button-border': {
                ...((theme.typography as any)?.button as any),

                '&.fb-px-3': {
                    px: 4,
                },

                '&.fb-py-3': {
                    px: 1,
                },
            },
        },

        '.RaBulkActionsToolbar-title': {
            alignItems: 'center',
        },
        '.RaDatagrid-root': {
            overflowX: 'auto',
            '.RaDatagrid-rowOdd': {
                //(theme) => ({
                backgroundColor: '#fcfcfc',
                //}),
            },
            '.RaDatagrid-row ': {
                '& > td:first-child, & > th:first-child': {
                    pl: 3,
                },
                '& > td:last-child, & > th:last-child': {
                    pr: 3,
                },
            },
        },
    }),
});

const appTheme: RaThemeOptions = responsiveFontSizes(
    createTheme(merge({}, defaultTheme, theme))
);

export default appTheme;
